import { createContext, useContext, useEffect, useState } from "react";
import { checkHealth } from "./api";

/**
 * Backend reachability, polled once for the whole app.
 *
 * Shared through context rather than polled per component: the navbar shows it
 * as a badge, and both tool pages need it to decide whether an upload is even
 * possible. Two independent pollers would disagree with each other for up to a
 * full interval, which would show as a green badge over a blocked dropzone.
 */
const HealthContext = createContext("checking");

const POLL_MS = 30000;

export function HealthProvider({ children }) {
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const ping = async () => {
      try {
        await checkHealth(controller.signal);
        if (!cancelled) setStatus("online");
      } catch {
        if (!cancelled) setStatus("offline");
      }
    };

    ping();
    const timer = window.setInterval(ping, POLL_MS);
    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(timer);
    };
  }, []);

  return <HealthContext.Provider value={status}>{children}</HealthContext.Provider>;
}

/** "checking" | "online" | "offline" */
export function useHealth() {
  return useContext(HealthContext);
}

export function useIsOffline() {
  return useHealth() === "offline";
}
